
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import TravelSelect from '@/components/inputs/TravelSelect.vue';
import DatePicker from '@/components/inputs/DatePicker.vue';
import Budget from '@/models/budget';
import moment from 'moment';
import WhoSelect from '@/components/inputs/WhoSelect.vue';
import ProvinceSelect from '@/components/inputs/ProvinceSelect.vue';
import GroupAgeSelect from '@/components/inputs/GroupAgeSelect.vue';
import { ESPERA_DE_PRECIOS, EsperaDePrecios, MEDIO_SEGUNDO } from '@/helpers/espera-de-precios';

/** N-288 · 5A: el texto gris bajo la salida, el de la maqueta. */
function textoSalidaMovida(dias: number, salidaDeAntes: string): string {
	const cuantos = dias === 1 ? '1 día' : `${dias} días`;
	return `Movida con la llegada: ${cuantos}, como antes (era el ${moment(salidaDeAntes, 'YYYY-MM-DD').format('DD/MM/YYYY')}).`;
}

@Component({
	components: {
		GroupAgeSelect,
		ProvinceSelect,
		WhoSelect,
		DatePicker,
		TravelSelect,
		ValidationProvider,
		ValidationObserver,
	},
})
export default class TabBudgetDestination extends Vue {
	@Prop({ default: null, required: true }) private budget;
	@Prop({ default: null, required: false }) private errors;
	@Inject({ from: ESPERA_DE_PRECIOS, default: null }) private readonly esperaDePrecios!: EsperaDePrecios | null;

	$refs!: {
		obs: InstanceType<typeof ValidationObserver>;
	};

	private destination_budget: any;
	private minDateStartModel: any;
	private maxDateStartModel: any;
	private minDateFinishModel: any;
	private maxDateFinishModel: any;
	// N-288 · 5A: la llegada sobre la que está contada la salida, y el texto que lo dice.
	private llegadaDeLaSalida: any;
	private avisoSalidaMovida: string;
	// N-288 · 4A: la espera de medio segundo tras la última tecla de alumnos o profesores.
	private temporizadorPasajeros: any = null;

	constructor() {
		super();
		this.destination_budget = new Budget();
		this.minDateStartModel = '';
		this.maxDateStartModel = '';
		this.minDateFinishModel = '';
		this.maxDateFinishModel = '';
		this.llegadaDeLaSalida = null;
		this.avisoSalidaMovida = '';
	}

	mounted() {
		if (this.budget) {
			this.destination_budget = Budget.copy(this.budget);
			this.minDateStartModel =  moment().format('YYYY-MM-DD');
			this.maxDateStartModel = moment(this.budget.travel_season_end, 'YYYY-MM-DD').format('YYYY-MM-DD');
			// Al abrir la ficha no se mueve ni se borra nada: solo los límites del calendario.
			this.llegadaDeLaSalida = this.destination_budget.start_date;
			this.ponerLimitesDeLaSalida();
		}
	}

	ponerLimitesDeLaSalida() {
		this.minDateFinishModel = moment(this.destination_budget.start_date, 'YYYY-MM-DD').add(this.budget.travel_duration_min, 'days').format('YYYY-MM-DD');
		this.maxDateFinishModel = moment(this.destination_budget.start_date, 'YYYY-MM-DD').add(this.budget.travel_duration_max - 1, 'days').format('YYYY-MM-DD');
	}

	/**
	 * El `@input` del calendario de la llegada. N-288 · 5A («Siempre», Bernardo, 7-oct): la
	 * salida se mueve con la llegada conservando los días del viaje, también si no se
	 * cruzan y también si el viaje es más largo de lo que deja elegir el calendario
	 * (8-oct). Antes la salida se VACIABA si la llegada la pasaba, sin decir nada.
	 * Sin salida, se queda vacía.
	 */
	setEndDateLimits() {
		const llegada = this.destination_budget.start_date;
		const salida = this.destination_budget.end_date;
		const antes = this.llegadaDeLaSalida;
		if (llegada && salida && antes && llegada !== antes) {
			const noches = moment(salida, 'YYYY-MM-DD').diff(moment(antes, 'YYYY-MM-DD'), 'days');
			this.destination_budget.end_date = moment(llegada, 'YYYY-MM-DD').add(noches, 'days').format('YYYY-MM-DD');
			this.avisoSalidaMovida = textoSalidaMovida(noches + 1, salida);
		}
		this.llegadaDeLaSalida = llegada;
		this.ponerLimitesDeLaSalida();
	}

	/**
	 * El `@input` de alumnos y profesores. N-288 · 4A (Bernardo, 7-oct): los precios se piden medio
	 * segundo después de la última tecla —teclear «57» pedía los de 5 y los de 57—, y mientras tanto
	 * GUARDAR espera con el número tecleado. La espera NO va dentro de `emitValue()`: lo llaman los
	 * tests de caracterización y otras piezas que quieren el destino al momento.
	 */
	pasajerosTecleados() {
		clearTimeout(this.temporizadorPasajeros);
		this.temporizadorPasajeros = setTimeout(() => this.soltarPasajeros(), MEDIO_SEGUNDO);
		if (this.esperaDePrecios) {
			this.esperaDePrecios.tecleando = parseInt(this.destination_budget.students) + parseInt(this.destination_budget.teachers);
		}
	}

	soltarPasajeros() {
		this.cancelarEsperaDeTeclas();
		this.emitValue();
	}

	cancelarEsperaDeTeclas() {
		clearTimeout(this.temporizadorPasajeros);
		this.temporizadorPasajeros = null;
		if (this.esperaDePrecios) {
			this.esperaDePrecios.tecleando = null;
		}
	}

	beforeDestroy() {
		this.cancelarEsperaDeTeclas();
	}

	async validate() {
		// Pasar a otra pestaña o GUARDAR lleva el número tecleado al momento (y una sola vez).
		this.cancelarEsperaDeTeclas();
		this.$emit('input', this.destination_budget);
		return await this.$refs.obs.validate();
	}

	emitValue() {
		// Esto es para actualizar el precio con el suplemento de profesores
		this.$emit('input', this.destination_budget);
	}
}
